import { Children, createContext, Fragment, isValidElement, useEffect, useLayoutEffect } from "react";
import { Div, type DivProps } from "components/primitives/Div";
import useCarousel from "../hooks/useCarousel";
import useCarouselActions from "../hooks/useCarouselActions";

export const CarouselSlideIndexContext = createContext<number | undefined>(undefined);
CarouselSlideIndexContext.displayName = "CarouselSlideIndexContext";

/** `id` is reserved: the navigation buttons point `aria-controls` at the generated one. */
export interface CarouselSlidesProps extends Omit<DivProps, "id"> {}

/**
 * @description Holds the `<CarouselSlide />` elements. Slide changes are announced politely,
 * as the carousel never rotates on its own.
 *
 * Slides must be direct children: wrapped in a Fragment or an element, they count as a single slide.
 */
function CarouselSlides(props: CarouselSlidesProps) {
  const { children, ...rest } = props;
  const { slidesId } = useCarousel();
  const { setSteps } = useCarouselActions();

  const slides = Children.toArray(children).filter(isValidElement);
  const hasFragment = slides.some((slide) => slide.type === Fragment);

  /** Runs before paint so the stepper and the controls never show a stale count. */
  useLayoutEffect(() => {
    setSteps(slides.length);
  }, [setSteps, slides.length]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && hasFragment) {
      console.error("CarouselSlides: slides must be direct children. A Fragment counts as a single slide.");
    }
  }, [hasFragment]);

  return (
    <Div aria-live="polite" {...rest} id={slidesId}>
      {slides.map((slide, index) => (
        <CarouselSlideIndexContext.Provider key={slide.key} value={index}>
          {slide}
        </CarouselSlideIndexContext.Provider>
      ))}
    </Div>
  );
}

export default CarouselSlides;
